import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Users as UsersIcon, MapPin, Calendar as CalendarIcon,
  ClipboardList, Trophy, Dumbbell, ChevronRight
} from 'lucide-react';
import PaginationBar, { usePagedRows } from '@/components/PaginationBar';

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Seguimiento = () => {
  usePageTitle('Seguimiento');
  const { isScout, user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const currentUserId = user?.id;

  useEffect(() => {
    fetchRecords();
  }, [filterType]); // eslint-disable-line

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== 'all') params.event_type = filterType;
      const res = await axios.get(`${API}/tracking`, { params });
      setRecords(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar seguimientos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-4 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Seguimiento</h1>
            <p className="text-sm text-gray-500 mt-1">
              {isScout ? 'Registro de visitas a partidos y prácticas' : 'Historial de visitas del equipo de scouts'}
            </p>
          </div>
          <Button
            onClick={() => navigate('/seguimiento/nuevo')}
            className="bg-brand-600 hover:bg-brand-700 w-full sm:w-auto"
            data-testid="btn-nuevo-seguimiento"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo seguimiento
          </Button>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-60" data-testid="filter-event-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los eventos</SelectItem>
              <SelectItem value="partido">Solo partidos</SelectItem>
              <SelectItem value="entrenamiento">Solo prácticas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Records */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600"></div>
          </div>
        ) : records.length === 0 ? (
          <Card className="p-10 text-center">
            <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No hay seguimientos registrados</p>
            <p className="text-sm text-gray-400 mt-1">Presioná "Nuevo seguimiento" para empezar</p>
          </Card>
        ) : (
          <SeguimientoList records={records} isScout={isScout} currentUserId={currentUserId} />
        )}
      </div>
    </Layout>
  );
};

export default Seguimiento;

function SeguimientoList({ records, isScout, currentUserId }) {
  const { paged, pagination } = usePagedRows(records, { initialPageSize: 25 });

  // Calcular resumen para el scout: cuántas hizo él vs cuántas son a sus jugadores hechas por otros
  const summary = React.useMemo(() => {
    if (!isScout || !currentUserId) return null;
    const own = records.filter(r => r.scout_id === currentUserId).length;
    const externalToMine = records.length - own;
    return { own, externalToMine, total: records.length };
  }, [records, isScout, currentUserId]);

  return (
    <>
      {summary && summary.total > 0 && (
        <Card className="p-3 mb-3 bg-gradient-to-r from-brand-50 to-blue-50 border-brand-200" data-testid="seguimiento-summary">
          <p className="text-sm text-gray-700">
            <strong>{summary.total}</strong> visitas en total ·{' '}
            <span className="text-emerald-700"><strong>{summary.own}</strong> hechas por vos</span> ·{' '}
            <span className="text-blue-700"><strong>{summary.externalToMine}</strong> a tus jugadores por otros scouts</span>
          </p>
        </Card>
      )}
      <div className="space-y-3">
        {paged.map((r) => {
          const isPartido = r.event_type === 'partido';
          const EventIcon = isPartido ? Trophy : Dumbbell;
          const isExternal = isScout && currentUserId && r.scout_id !== currentUserId;
          return (
            <Link
              key={r.id}
              to={`/seguimiento/${r.id}`}
              data-testid={`tracking-card-${r.id}`}
              className="block"
            >
              <Card className={`p-4 hover:shadow-md transition-shadow active:scale-[0.99] ${isExternal ? 'border-blue-300 bg-blue-50/30' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className={`shrink-0 p-2.5 rounded-lg ${isPartido ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    <EventIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {isPartido ? 'Partido' : 'Práctica'}
                      </Badge>
                      {isExternal && (
                        <Badge className="text-xs bg-blue-100 text-blue-800 border border-blue-300">
                          👁️ A tu jugador
                        </Badge>
                      )}
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {formatDate(r.date)}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mt-1 truncate">
                      {r.club || 'Club sin especificar'}
                      {r.opponent && <span className="text-gray-500 font-normal"> vs {r.opponent}</span>}
                    </h3>
                    {r.location && (
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" />
                        {r.location}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-600 flex-wrap">
                      <span className="flex items-center gap-1">
                        <UsersIcon className="w-3 h-3" />
                        {(r.players_observed || []).length} {(r.players_observed || []).length === 1 ? 'jugador' : 'jugadores'} observado{(r.players_observed || []).length === 1 ? '' : 's'}
                      </span>
                      {r.scout_name && (
                        <span className={isExternal ? 'text-blue-700 font-semibold' : 'text-gray-400 truncate'}>
                          {isExternal ? 'Por: ' : (!isScout ? 'Scout: ' : 'Por vos')}
                          {!isScout || isExternal ? r.scout_name : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 mt-2" />
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
      <PaginationBar {...pagination} />
    </>
  );
}
